'use client'
import { LoginAfterResetAction } from '@/actions'
import { ButtonRoundStyle, RHFTextField } from '@/components'
import { Form } from '@/components/form/form-provider'
import { CALLBACK_URL_KEY } from '@/constants'
import { setRef } from '@/features/ref/utils/setRef'
import { useAccountStore, useCartStore, useCompareStore } from '@/store'
import { DEFAULT_LOGIN_REDIRECT } from '@/utils/routes'
import yup from '@/utils/yup-global'
import { resetPwSchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

type Props = {
  email: string
  otp: string
}

export const ResetPasswordForm = ({ email, otp }: Props) => {
  const [isPending, startTransition] = useTransition()
  const t = useTranslations('UserProfile')
  const router = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get(CALLBACK_URL_KEY)

  // Store hooks để update user data sau login
  const { setUser } = useAccountStore()
  const { mergeCart } = useCartStore()
  const { assignToUser } = useCompareStore()

  const method = useForm<yup.InferType<typeof resetPwSchema>>({ resolver: yupResolver(resetPwSchema) })
  const { handleSubmit } = method

  const onSubmit = (data: yup.InferType<typeof resetPwSchema>) => {
    startTransition(async () => {
      try {
        // Thực hiện reset password và auto login
        const result = await LoginAfterResetAction(email, otp, data.password, callbackUrl)

        if (result.status === 'error') {
          toast.error(result.message)
          return
        }

        // Cập nhật thông tin user và merge data giống như login bình thường
        setUser(result?.data ?? {})
        mergeCart()
        assignToUser()

        const redirectUrl = callbackUrl ?? DEFAULT_LOGIN_REDIRECT
        const refValue = result?.data?.increment_id
        const redirectUrlWithRef = setRef(refValue, redirectUrl)
        // router.push(redirectUrlWithRef)
        window.location.href = redirectUrlWithRef
      } catch (error) {
        console.error('Reset password error:', error)
        toast.error('Có lỗi xảy ra khi đặt lại mật khẩu')
      }
    })
  }

  return (
    <Form methods={method} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <RHFTextField
          autoFocus
          name="password"
          type="password"
          label={t('Mật khẩu mới')}
          placeholder={t('Nhập mật khẩu mới')}
          disabled={isPending}
        />
        <RHFTextField
          name="confirmPassword"
          type="password"
          label={t('Xác nhận mật khẩu mới')}
          placeholder={t('Nhập lại mật khẩu mới')}
          disabled={isPending}
        />
        <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} loading={isPending}>
          {t('Đặt lại mật khẩu')}
        </ButtonRoundStyle>
      </Stack>
    </Form>
  )
}
